import { bytes32Schema, launchAmountSchema, nonzeroAddressSchema, uint256StringSchema, type Address, type Hex, type PreparedCall } from '@hoodmorning/contracts';
import { encodeFunctionData, getAddress, isAddressEqual, parseAbi, type PublicClient } from 'viem';
import { hashPreparedCalls } from './calls.js';
import { verifyContractCode } from './codeVerification.js';

export const curveTradeAbi = parseAbi([
  'function factory() view returns (address)',
  'function quote() view returns (address)',
  'function token() view returns (address)',
  'function policyHash() view returns (bytes32)',
  'function lifecycle() view returns (uint8)',
  'function buy(uint128 maximumGross,uint128 minimumBaseOut,uint16 maximumFeeBps,uint256 deadline,address recipient) returns ((uint128 spentGross,uint128 netQuote,uint128 feeQuote,uint128 refund,uint128 baseOut,uint16 totalFeeBps,uint256 tier))',
  'function sell(uint128 baseAmount,uint128 minimumQuoteOut,uint16 maximumFeeBps,uint256 deadline,address recipient) returns ((uint128 grossQuote,uint128 netQuote,uint128 feeQuote,uint16 totalFeeBps,uint256 tier))',
]);
const factoryRegistryAbi = parseAbi(['function isLaunchCurve(address curve) view returns (bool)', 'function curveForToken(address token) view returns (address)']);
export const curveApprovalAbi = parseAbi(['function approve(address spender,uint256 amount) returns (bool)']);

export type CurveTradeRequest = {
  vault: Address;
  curve: Address;
  baseToken: Address;
  quoteToken: Address;
  policyHash: Hex;
  side: 'BUY' | 'SELL';
  amountIn: bigint;
  minimumAmountOut: bigint;
  maximumFeeBps: number;
  deadline: bigint;
};

/** Returns one ATOMIC vault batch; do not send its approval calls as separate transactions. */
export function encodeCurveTradeBatch(request: CurveTradeRequest, nowUnixSeconds: bigint) {
  for (const address of [request.vault, request.curve, request.baseToken, request.quoteToken]) nonzeroAddressSchema.parse(address);
  bytes32Schema.parse(request.policyHash);
  if (request.side !== 'BUY' && request.side !== 'SELL') throw new Error('Unsupported curve trade side');
  launchAmountSchema.parse(request.amountIn.toString());
  launchAmountSchema.parse(request.minimumAmountOut.toString());
  uint256StringSchema.parse(request.deadline.toString());
  if (request.deadline <= nowUnixSeconds) throw new Error('Curve trade expired');
  if (!Number.isInteger(request.maximumFeeBps) || request.maximumFeeBps < 0 || request.maximumFeeBps >= 10_000) throw new Error('Invalid maximum fee');
  if (isAddressEqual(request.baseToken, request.quoteToken)) throw new Error('Base and quote must differ');
  const approvalToken = request.side === 'BUY' ? request.quoteToken : request.baseToken;
  const approve = (amount: bigint): PreparedCall => ({ to: getAddress(approvalToken), value: 0n, data: encodeFunctionData({ abi: curveApprovalAbi, functionName: 'approve', args: [request.curve, amount] }) });
  const calls: PreparedCall[] = [approve(0n), approve(request.amountIn), {
    to: getAddress(request.curve), value: 0n,
    data: encodeFunctionData({ abi: curveTradeAbi, functionName: request.side === 'BUY' ? 'buy' : 'sell', args: [request.amountIn, request.minimumAmountOut, request.maximumFeeBps, request.deadline, request.vault] }),
  }, approve(0n)];
  return { calls, callsHash: hashPreparedCalls(calls), executionSource: 'launch-curve' as const, cashReferralAmount: 0n, terminalApplicationFeeAmount: 0n };
}

/** Rechecks canonical source at one block. This is NOT simulation or permission to submit. */
export async function prepareCanonicalCurveTrade(input: {
  client: Pick<PublicClient, 'getChainId' | 'getBlock' | 'getBytecode' | 'readContract'>;
  factory: { address: Address; codeHash: Hex };
  request: CurveTradeRequest;
  nowUnixSeconds: bigint;
}) {
  // Keep the encoded batch and all source verification on the same immutable request snapshot.
  const request = { ...input.request };
  const pinnedFactory = { ...input.factory };
  const nowUnixSeconds = input.nowUnixSeconds;
  nonzeroAddressSchema.parse(pinnedFactory.address);
  bytes32Schema.parse(pinnedFactory.codeHash);
  const batch = encodeCurveTradeBatch(request, nowUnixSeconds);
  if (await input.client.getChainId() !== 4663) throw new Error('RPC chain mismatch');
  const block = await input.client.getBlock();
  if (block.number === null || block.hash === null) throw new Error('Canonical source block unavailable');
  if (block.timestamp > nowUnixSeconds + 30n || nowUnixSeconds > block.timestamp + 120n) throw new Error('Canonical source block is stale or future-dated');
  const blockNumber = block.number;
  const report = await verifyContractCode({ getBytecode: parameters => input.client.getBytecode({ address: parameters.address, blockNumber }) }, [{ name: 'launchFactory', address: pinnedFactory.address, expectedCodeHash: pinnedFactory.codeHash }]);
  if (!report.valid) throw new Error(`Factory code verification failed: ${report.failures.join(',')}`);
  const curve = request.curve;
  const [registered, tokenCurve, factory, baseToken, quoteToken, policyHash, lifecycle] = await Promise.all([
    input.client.readContract({ address: pinnedFactory.address, abi: factoryRegistryAbi, functionName: 'isLaunchCurve', args: [curve], blockNumber }),
    input.client.readContract({ address: pinnedFactory.address, abi: factoryRegistryAbi, functionName: 'curveForToken', args: [request.baseToken], blockNumber }),
    input.client.readContract({ address: curve, abi: curveTradeAbi, functionName: 'factory', blockNumber }),
    input.client.readContract({ address: curve, abi: curveTradeAbi, functionName: 'token', blockNumber }),
    input.client.readContract({ address: curve, abi: curveTradeAbi, functionName: 'quote', blockNumber }),
    input.client.readContract({ address: curve, abi: curveTradeAbi, functionName: 'policyHash', blockNumber }),
    input.client.readContract({ address: curve, abi: curveTradeAbi, functionName: 'lifecycle', blockNumber }),
  ]);
  if (!registered || !isAddressEqual(tokenCurve, curve) || !isAddressEqual(factory, pinnedFactory.address)) throw new Error('Curve is not canonical for the pinned factory');
  if (!isAddressEqual(baseToken, request.baseToken) || !isAddressEqual(quoteToken, request.quoteToken)) throw new Error('Curve asset mismatch');
  if (policyHash.toLowerCase() !== request.policyHash.toLowerCase()) throw new Error('Launch policy mismatch');
  if (lifecycle !== 0) throw new Error('Curve trading is closed');
  const recheck = await input.client.getBlock({ blockNumber });
  if (recheck.hash !== block.hash) throw new Error('Source block changed during preparation');
  if (await input.client.getChainId() !== 4663) throw new Error('RPC chain changed during preparation');
  return { ...batch, chainId: 4663 as const, vault: getAddress(request.vault), sourceBlockNumber: blockNumber, sourceBlockHash: block.hash, simulationStatus: 'required' as const };
}
